/**
 * Entrance reveal — uses PlayCanvas GsplatRevealRadial as a Script on the gsplat entity.
 *
 * Pop-free strategy ("held-at-zero"): the reveal script is enabled the moment the gsplat
 * entity exists, but its `effectTime` is frozen at 0 (`HoldableReveal.hold`). At t=0 the
 * reveal shader scales every splat to zero, so the splats are fully invisible while the
 * shader is already live on the draw material — there is never a frame where un-revealed
 * splats render in full colour. When the user is ready we release the hold and the SAME
 * shader advances from exactly t=0, producing one single continuous wave (no re-enable,
 * so `GsplatShaderEffect` never resets the clock and no second wave appears).
 */
import * as pc from 'playcanvas';
import { GsplatRevealRadial } from 'playcanvas/scripts/esm/gsplat/reveal-radial.mjs';
import { revealLiftWaveRadius } from '@viewer/playcanvas/entrancePhaseGuards';
import { entranceTrace } from '@viewer/playcanvas/entranceTrace';
import { remainingWorkProgress, VISIBLE_PROGRESS_END } from '@viewer/playcanvas/entranceProgress';

export type WorldEntrancePhase = 'welcome' | 'dots' | 'loading' | 'reveal' | 'done';

/** Matches playcanvas/examples/.../lod-streaming.example.mjs reveal defaults. */
export interface WorldEntranceRevealConfig {
  endRadius: number;
  speed: number;
  acceleration: number;
  delay: number;
  oscillationIntensity: number;
  bandWidth: number;
}

export const DEFAULT_WORLD_ENTRANCE_REVEAL: WorldEntranceRevealConfig = {
  endRadius: 45,
  speed: 6.25,
  acceleration: 0.65,
  delay: 2,
  oscillationIntensity: 0.2,
  bandWidth: 1,
};

export const PRE_ASSET_PROGRESS_CAP = 0.88;
export const ASSET_READY_PROGRESS_FLOOR = 0.94;
const AMBIENT_PROGRESS_CAP = 0.55;

export class WorldEntrance {
  /**
   * effectTime the reveal is pinned to while held. Negative so the dot-wave position
   * (`speed·t`) is negative and every splat is scaled to zero — fully invisible. One frame
   * of the engine's own script update may advance it by ~dt before {@link tickHold} re-pins,
   * so the magnitude is comfortably larger than a frame's worth of wave travel.
   */
  static readonly HELD_EFFECT_TIME = -1;

  private _phase: WorldEntrancePhase = 'welcome';
  private readonly config: WorldEntranceRevealConfig;
  private revealScript: GsplatRevealRadial | null = null;
  private revealEntity: pc.Entity | null = null;
  private revealStarted = false;
  /**
   * While true the reveal shader is applied (splats invisible at effectTime 0) but the
   * effect clock is pinned to 0 each frame, so nothing animates yet. Releasing the hold
   * lets the SAME enabled script advance from 0 → a single continuous wave.
   */
  private revealHeld = false;

  private loadBaselineSplats = 0;
  private loadTargetSplats = 1;
  private loadBaselineAssetProgress = 0;
  private assetLoadProgress = 0;
  private displayedLoadProgress = 0;
  private assetReady = false;

  constructor(_app: pc.AppBase, config: Partial<WorldEntranceRevealConfig> = {}) {
    this.config = { ...DEFAULT_WORLD_ENTRANCE_REVEAL };
    for (const [key, value] of Object.entries(config) as [
      keyof WorldEntranceRevealConfig,
      number | undefined,
    ][]) {
      if (value !== undefined) this.config[key] = value;
    }
  }

  get phase(): WorldEntrancePhase {
    return this._phase;
  }

  get isActive(): boolean {
    return this._phase !== 'done';
  }

  get loadProgress(): number {
    return this.displayedLoadProgress;
  }

  get revealEffectTimeS(): number {
    return this.revealScript?.effectTime ?? 0;
  }

  get revealCompletionTimeS(): number {
    return this.revealScript?.getCompletionTime() ?? 0;
  }

  get revealConfig(): Readonly<WorldEntranceRevealConfig> {
    return this.config;
  }

  /**
   * Current radius (world units) of the leading dot wave-front, derived from the same
   * kinematics the reveal shader uses: `pos = speed·t + ½·accel·t²`. Drives the preview-dot
   * dissolve so points convert to splats exactly where the wave is.
   */
  get revealWaveRadius(): number {
    const { speed, acceleration } = this.config;
    return revealLiftWaveRadius({
      effectTime: this.revealScript?.effectTime ?? 0,
      delay: this.config.delay,
      speed,
      acceleration,
    });
  }

  get revealScriptEnabled(): boolean {
    return this.revealScript?.enabled ?? false;
  }

  private revealPhaseElapsedS = 0;

  skip(): void {
    if (this.revealScript) this.revealScript.enabled = false;
    this._phase = 'done';
    this.displayedLoadProgress = 1;
  }

  beginDots(): void {
    if (this._phase === 'done') return;
    this._phase = 'dots';
  }

  beginLoading(baselineSplats: number, targetSplats: number, assetProgress = 0): void {
    if (this._phase === 'done') return;
    this._phase = 'loading';
    this.loadBaselineSplats = Math.max(0, baselineSplats);
    this.loadTargetSplats = Math.max(this.loadBaselineSplats + 1, targetSplats);
    this.loadBaselineAssetProgress = Math.min(0.999, Math.max(0, assetProgress));
    this.assetLoadProgress = Math.max(this.assetLoadProgress, this.loadBaselineAssetProgress);
    this.displayedLoadProgress = remainingWorkProgress(
      this.loadBaselineSplats,
      this.loadBaselineSplats,
      this.loadTargetSplats,
    );
  }

  setAssetReady(ready: boolean): void {
    this.assetReady = ready;
  }

  setAssetLoadProgress(progress: number): void {
    this.assetLoadProgress = Math.min(1, Math.max(this.assetLoadProgress, progress));
  }

  tickLoadProgress(renderedSplats: number, dt: number): void {
    if (
      this._phase !== 'welcome' &&
      this._phase !== 'dots' &&
      this._phase !== 'loading' &&
      this._phase !== 'reveal'
    ) {
      return;
    }
    const assetProgressSinceBar = remainingWorkProgress(
      this.assetLoadProgress,
      this.loadBaselineAssetProgress,
      1,
    );
    const ambientTarget = Math.min(AMBIENT_PROGRESS_CAP, this.displayedLoadProgress + dt * 0.012);
    const preAssetTarget = Math.min(
      PRE_ASSET_PROGRESS_CAP,
      Math.max(ambientTarget, assetProgressSinceBar * PRE_ASSET_PROGRESS_CAP),
    );
    const renderedTarget = remainingWorkProgress(
      renderedSplats,
      this.loadBaselineSplats,
      this.loadTargetSplats,
    );
    const target = this.assetReady
      ? Math.max(ASSET_READY_PROGRESS_FLOOR, renderedTarget)
      : preAssetTarget;

    if (target > this.displayedLoadProgress) {
      const eased = this.displayedLoadProgress + (target - this.displayedLoadProgress) * 0.08;
      const stepped = this.displayedLoadProgress + dt * (this.assetReady ? 0.5 : 0.08);
      this.displayedLoadProgress = Math.min(target, Math.max(eased, stepped));
    }

    if (this._phase !== 'reveal') {
      this.displayedLoadProgress = Math.min(this.displayedLoadProgress, VISIBLE_PROGRESS_END);
    }
  }

  /**
   * Attach GsplatRevealRadial and immediately enable it in the HELD state: the shader is
   * applied (so splats render invisibly, scaled to 0 at effectTime 0) but its clock is
   * pinned to 0 by {@link tickHold}. This guarantees there is never a frame of fully
   * coloured, un-revealed splats. {@link startReveal} later releases the hold.
   */
  attachRevealScript(gsplatEntity: pc.Entity, revealCenter: pc.Vec3): void {
    if (this.revealScript) return;
    if (!gsplatEntity.script) gsplatEntity.addComponent('script');

    const script = gsplatEntity.script!.create(
      GsplatRevealRadial as unknown as typeof pc.Script,
    ) as unknown as GsplatRevealRadial;

    const c = this.config;
    // PlayCanvas's reveal examples and our stripped harness pass this as a world-space
    // focus for both WebGPU and WebGL/CPU paths.
    script.center.copy(revealCenter);
    script.speed = c.speed;
    script.acceleration = c.acceleration;
    script.delay = c.delay;
    script.dotTint.copy(new pc.Color(0.22, 0.21, 0.18));
    script.waveTint.copy(new pc.Color(0.78, 0.74, 0.62));
    script.oscillationIntensity = c.oscillationIntensity;
    script.endRadius = c.endRadius;
    script.bandWidth = c.bandWidth;
    // Enable now so the reveal shader chunk binds to the gsplat material before the first
    // splat render. Held at a negative effectTime the dot-wave position is negative, so
    // every splat is scaled to zero — fully invisible, no pop, no centre blob.
    script.enabled = true;
    script.effectTime = WorldEntrance.HELD_EFFECT_TIME;

    // The reveal's `gsplatModifyVS` chunk runs when splats are written to the work buffer
    // (gsplatCopyToWorkbuffer), which the hybrid GPU-sort renderer (WebGPU default since
    // engine 2.20.0) only does on demand. A time-varying effect (uTime) therefore needs the
    // work buffer re-rendered every frame, or the wave is written once and frozen. CPU sort
    // ignores this flag (harmless). Restored to AUTO when the reveal completes.
    const comp = gsplatEntity.gsplat as unknown as { workBufferUpdate?: number } | undefined;
    if (comp) comp.workBufferUpdate = pc.WORKBUFFER_UPDATE_ALWAYS;

    this.revealScript = script;
    this.revealEntity = gsplatEntity;
    this.revealHeld = true;
    entranceTrace('reveal script attached + held at t=0', {
      revealCenter: `${revealCenter.x.toFixed(2)},${revealCenter.y.toFixed(2)},${revealCenter.z.toFixed(2)}`,
      speed: c.speed,
      delay: c.delay,
      endRadius: c.endRadius,
    });
  }

  /** True while the reveal shader is applied but frozen at t=0 (splats invisible). */
  get isRevealHeld(): boolean {
    return this.revealHeld;
  }

  /**
   * Whether the reveal shader chunk is actually live on the gsplat material yet. We check
   * the *script's own* `material` reference — `GsplatShaderEffect.applyShaders()` sets it
   * only after running once with a loaded gsplat, which is exactly when the `gsplatModifyVS`
   * chunk has been written. (Do not fall back to `app.scene.gsplat.material`: that object
   * exists from construction and would report ready before the chunk is applied.)
   */
  isRevealShaderApplied(): boolean {
    return Boolean(this.revealScript?.material);
  }

  /**
   * Pin the held reveal to a negative effectTime every frame. The gsplat script advances
   * its own clock during the engine's script update (which runs before our app update), so
   * we re-pin here to keep every splat scaled to zero (fully invisible) without animating.
   */
  tickHold(): void {
    if (this.revealHeld && this.revealScript) {
      this.revealScript.effectTime = WorldEntrance.HELD_EFFECT_TIME;
    }
  }

  /** Release the hold: the already-enabled script advances from 0 into one single wave. */
  startReveal(): void {
    if (!this.revealScript || this._phase === 'reveal' || this._phase === 'done') return;
    this.revealPhaseElapsedS = 0;
    this.revealHeld = false;
    this.revealScript.effectTime = 0;
    this.revealScript.enabled = true;
    this.revealStarted = true;
    this._phase = 'reveal';
    this.displayedLoadProgress = 1;
    entranceTrace('reveal hold released — single wave begins', {
      effectTimeS: this.revealScript.effectTime,
      hasMaterial: Boolean(this.revealScript.material),
    });
  }

  /**
   * Stop forcing the per-frame work-buffer re-render once the reveal is done — leaving it on
   * `ALWAYS` would re-render all splats every frame forever, defeating render-on-demand.
   */
  private restoreWorkBufferUpdate(): void {
    const comp = this.revealEntity?.gsplat as unknown as { workBufferUpdate?: number } | undefined;
    if (comp) comp.workBufferUpdate = pc.WORKBUFFER_UPDATE_AUTO;
  }

  /** Returns true once the script has finished and removed itself. */
  tick(dt: number): boolean {
    if (this._phase !== 'reveal' || !this.revealScript || !this.revealStarted) return false;

    this.revealPhaseElapsedS += dt;
    const effectTime = this.revealScript.effectTime;

    if (this.revealScript.isEffectComplete()) {
      this._phase = 'done';
      this.restoreWorkBufferUpdate();
      entranceTrace('reveal script complete', { effectTimeS: effectTime.toFixed(3) });
      return true;
    }

    // Script self-disables when done — only treat as complete if the effect actually ran.
    if (!this.revealScript.enabled && effectTime > 0.5) {
      this._phase = 'done';
      this.restoreWorkBufferUpdate();
      entranceTrace('reveal script disabled after run', { effectTimeS: effectTime.toFixed(3) });
      return true;
    }

    // Shader never attached — don't strand the user without UI.
    if (this.revealPhaseElapsedS > 10 && effectTime < 0.05) {
      this.revealScript.enabled = false;
      this._phase = 'done';
      this.restoreWorkBufferUpdate();
      entranceTrace('reveal TIMEOUT — shader never ran, forcing done', {
        elapsedS: this.revealPhaseElapsedS.toFixed(1),
      });
      return true;
    }

    return false;
  }

  forceDone(): void {
    if (this.revealScript) this.revealScript.enabled = false;
    this.restoreWorkBufferUpdate();
    this._phase = 'done';
    this.displayedLoadProgress = 1;
    entranceTrace('entrance force-done');
  }

  dispose(): void {
    if (this.revealEntity?.script) {
      try {
        this.revealEntity.script.destroy(GsplatRevealRadial.scriptName);
      } catch {
        // entity may already be destroyed
      }
    }
    this.revealScript = null;
    this.revealEntity = null;
    this._phase = 'done';
  }
}
